import type {
  ApolloClient,
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client'
import type { FilterDefinition } from '@factorialco/f0-react/dist/experimental'
import * as Sentry from '@sentry/browser'

interface AsyncFilterWithQueryOptions<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> {
  label: string
  apolloClient: ApolloClient<unknown>
  query: DocumentNode | TypedDocumentNode<TData, TVariables>
  variables?: TVariables
  mapData: (data: TData) => Array<{ label: string; value: string | number }>
}

export const createAsyncFilterWithQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>({
  label,
  apolloClient,
  query,
  variables,
  mapData,
}: AsyncFilterWithQueryOptions<TData, TVariables>): FilterDefinition => ({
  label,
  type: 'in',
  options: {
    cache: true,
    options: async () => {
      try {
        const result = await apolloClient.query({
          query,
          variables,
          errorPolicy: 'all',
          fetchPolicy: 'cache-first',
        })

        if (result.error) {
          Sentry.captureException(result.error, {
            tags: { component: 'async-filter' },
            extra: { filter: label },
          })
          return []
        }

        return mapData(result.data)
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'async-filter' },
          extra: { filter: label },
        })
        return [] // Graceful degradation on unexpected errors
      }
    },
  },
})
